/**
 * ServiceNoteTemplatesPicker
 * -----------------------------------------------------------------------------
 * Dropdown of pre-saved service-note snippets the SM can drop into the
 * invoice's notes editor. Includes a few hard-coded "starter" presets so a
 * brand-new install isn't empty, plus everything saved at
 * `/api/templates?category=service_note`. Also exposes a "Save current notes
 * as template" button so the SM can build their own library quickly.
 */
import { useEffect, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, Plus, Save, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STARTER_PRESETS = [
  {
    name: 'Thank you',
    content:
      '<p><strong>Thank you for choosing Hoppers Crossing Service Centre.</strong> We appreciate your business and look forward to seeing you again.</p>',
  },
  {
    name: 'Roadworthy repairs required',
    content:
      '<p><strong>The following repairs are required for Roadworthy Certificate (RWC) compliance.</strong> Please review and confirm so we can schedule the work.</p>',
  },
  {
    name: 'Awaiting your approval',
    content:
      '<p><strong>Please review the items above and reply YES to approve so we can proceed.</strong> Parts are subject to availability at the time of approval.</p>',
  },
  {
    name: 'Next service due',
    content:
      '<p>Your next service is recommended in <strong>6 months / 10,000 km</strong>, whichever comes first. We\'ll send you a reminder closer to the time.</p>',
  },
  {
    name: 'Warranty note',
    content:
      '<p>Workmanship and parts fitted by us are covered by our standard <strong>12-month / 20,000 km</strong> warranty.</p>',
  },
];

export default function ServiceNoteTemplatesPicker({ onInsert, currentNotes }) {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newContent, setNewContent] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      // iter121: switch to canonical template-library endpoint. Returns
      // {template_id, name, content:{body}, ...}. Legacy
      // `/api/templates?category=service_note` stored body directly on
      // `content` (string). Keep dual-source so older docs still appear
      // alongside new ones until a one-time migration is run.
      const newRes = await fetch(`${BACKEND_URL}/api/template-library?template_type=service_notes`, { credentials: 'include' });
      const legacyRes = await fetch(`${BACKEND_URL}/api/templates?category=service_note`, { credentials: 'include' });
      let merged = [];
      if (newRes.ok) {
        const d = await newRes.json();
        merged = (d.templates || []).map(t => ({
          template_id: t.template_id,
          name: t.name,
          content: (t.content || {}).body || '',
          source: 'library',
        }));
      }
      if (legacyRes.ok) {
        const d = await legacyRes.json();
        const legacy = (d.templates || d || []).map(t => ({
          template_id: t.template_id,
          name: t.name,
          content: typeof t.content === 'string' ? t.content : (t.content?.body || ''),
          source: 'legacy',
        }));
        // De-dupe by template_id (preferring library version)
        const seen = new Set(merged.map(x => x.template_id));
        legacy.forEach(t => { if (!seen.has(t.template_id)) merged.push(t); });
      }
      setTemplates(merged);
    } catch (_e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);
  // Refresh templates each time the dropdown re-opens, so a freshly-saved
  // template appears without a page reload. Effect prevents the React
  // "setState while rendering" warning that arose when fetch() ran inline
  // during render.
  useEffect(() => {
    if (open) load();
  }, [open]);

  const handleInsert = (content) => {
    onInsert?.(content);
    setOpen(false);
  };

  const openSave = () => {
    setNewName('');
    setNewContent(currentNotes || '');
    setSaveOpen(true);
  };

  const handleSaveNew = async () => {
    const name = newName.trim();
    const content = newContent.trim();
    if (!name || !content) {
      toast.error('Both name and content are required');
      return;
    }
    setSaving(true);
    try {
      // iter121: save to the canonical template-library so the new
      // template shows up in Super Admin → Templates → Service Notes
      // alongside SM-created ones.
      const res = await fetch(`${BACKEND_URL}/api/template-library`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_type: 'service_notes',
          name,
          content: { body: content },
          is_global: false,
        }),
      });
      if (res.ok) {
        toast.success(`Saved template "${name}"`);
        setSaveOpen(false);
        load();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save template');
      }
    } finally { setSaving(false); }
  };

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="service-notes-templates-button">
            <Sparkles className="h-3 w-3 mr-1" />
            Templates
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-72 max-h-96 overflow-y-auto" data-testid="service-notes-templates-menu">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-500">
            Starter Presets
          </DropdownMenuLabel>
          {STARTER_PRESETS.map((p) => (
            <DropdownMenuItem
              key={p.name}
              onClick={() => handleInsert(p.content)}
              className="text-xs cursor-pointer"
              data-testid={`sn-preset-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <FileText className="h-3 w-3 mr-2 text-slate-400" />
              {p.name}
            </DropdownMenuItem>
          ))}

          {templates.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-500">
                Your Saved Templates
              </DropdownMenuLabel>
              {templates.map(t => (
                <DropdownMenuItem
                  key={t.template_id}
                  onClick={() => handleInsert(t.content)}
                  className="text-xs cursor-pointer"
                  data-testid={`sn-template-${t.template_id}`}
                >
                  <FileText className="h-3 w-3 mr-2 text-purple-400" />
                  <span className="truncate">{t.name}</span>
                </DropdownMenuItem>
              ))}
            </>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={openSave}
            className="text-xs cursor-pointer text-purple-700"
            data-testid="sn-save-as-template"
          >
            <Plus className="h-3 w-3 mr-2" />
            Save current notes as template…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-lg" data-testid="sn-save-dialog">
          <DialogHeader>
            <DialogTitle>Save as Service-Note Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-500">Name</label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. RWC repairs required"
                data-testid="sn-template-name"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-500">Content (HTML allowed)</label>
              <Textarea
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                rows={6}
                placeholder="<p>Paste/edit the snippet…</p>"
                data-testid="sn-template-content"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSaveNew}
              disabled={saving || !newName.trim() || !newContent.trim()}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="sn-template-save"
            >
              <Save className="h-4 w-4 mr-1" />
              {saving ? 'Saving…' : 'Save Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
